<template>
  <AuthorBreadcrumbs />
  <Author />
  <AuthorArticlesList />
  <SubscriptionBlock
    :api-url="``"
    :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
  />
</template>

<script lang="ts" setup>
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { useHead } from '@unhead/vue';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { computed, onMounted, onBeforeMount, watchEffect } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { trackEvent } from '@/services/analytics';
  import { MF_NAME } from '@/app/const';
  import { useAuthorEntity } from '@/pages/author/entities/author';
  import Author from '@/pages/author/widgets/Author.vue';
  import AuthorArticlesList from '@/pages/author/widgets/AuthorArticlesList.vue';
  import AuthorBreadcrumbs from '@/pages/author/widgets/AuthorBreadcrumbs.vue';

  const { VITE_RUNTIME_SOGU_SERVICE_URL } = useRuntimeConfig()[MF_NAME];

  const router = useRouter();
  const route = useRoute();
  const authorId = computed(() => Number(route.params.id));
  const { author, getAuthor, loadingFailed } = useAuthorEntity();

  watchEffect(() => {
    if (loadingFailed.value) {
      // перенаправляем на главную страницу журнала, если загрузка автора не удалась.
      router.replace('/');
    }
  });

  const title = computed(() =>
    author.value ? `${author.value.fullName} — автор статей в Журнале Трипстера.` : 'Автор статей в Журнале Трипстера.',
  );

  const description = computed(
    () =>
      author.value?.description ||
      'Вдохновляем на путешествия, помогаем собраться в поездку и увидеть всё самое главное и необычное.',
  );

  const ogImage = computed(
    () => author.value?.avatarLink || 'https://experience.tripster.ru/static/i/tripster-share-logo.jpg',
  );

  useHead({
    title,
    link: [
      {
        rel: 'canonical',
        href: `https://experience.tripster.ru/journal/author/${authorId.value}/`,
      },
    ],
    meta: [
      {
        name: 'description',
        content: () => description.value,
      },
      {
        property: 'og:title',
        content: () => title.value,
      },
      {
        property: 'og:description',
        content: () => description.value,
      },
      {
        content: () => ogImage.value,
        property: 'og:image',
      },
      {
        name: 'twitter:card',
        content: () => (author.value?.avatarLink ? undefined : 'summary_large_image'),
      },
      {
        name: 'twitter:image',
        content: () => ogImage.value,
      },
    ],
  });

  onBeforeMount(() => getAuthor(authorId.value));
  onMounted(() => trackEvent('journal-author:pageview'));
</script>

<style lang="scss" scoped>
  // Нельзя удалять, хотя бы одно правило должно быть
  .container {
    padding-bottom: 0;
  }
</style>
